import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Folder,
  LayoutGrid,
  Video,
  Shield,
  Settings,
  LogOut,
  Plus,
  ChevronLeft,
  ChevronRight,
  Wallet,
  Coins,
  Home,
  Crown,
  MessageSquare,
  Camera,
  Users,
  Package,
  Clapperboard,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const NAV_ITEMS = [
  { label: "Marketing Studio", path: "/marketing-studio", icon: Clapperboard, badge: "NEW" },
  { label: "Home", path: "/dashboard", icon: Home },
  { label: "Projects", path: "/projects", icon: Folder },
  { label: "Templates", path: "/templates", icon: LayoutGrid },
  { label: "Photoshoot", path: "/photoshoot", icon: Camera },
  { label: "AI Image Generator", path: "/image-studio", icon: Sparkles, badge: "NEW" },
  { label: "Avatar Studio", path: "/avatar-studio", icon: Users, badge: "NEW" },
  { label: "Product Studio", path: "/product-studio", icon: Package, badge: "NEW" },
  { label: "Created Videos", path: "/my-recreations", icon: Video },
  { label: "Credits", path: "/credits", icon: Wallet },
  { label: "Plans", path: "/pricing", icon: Crown },
];

const ADMIN_ITEMS = [
  { label: "Admin Panel", path: "/admin", icon: Shield },
  { label: "Manage Templates", path: "/admin/templates", icon: Settings },
  { label: "Photoshoot Templates", path: "/admin/photoshoot-templates", icon: Camera },
  { label: "Avatar Management", path: "/admin/avatar-management", icon: Users },
  { label: "Credit Pricing", path: "/admin/credit-pricing", icon: Coins },
  { label: "Image Studio Pricing", path: "/admin/image-studio-pricing", icon: Sparkles },
  { label: "Demo Requests", path: "/admin/contact-submissions", icon: MessageSquare },
];

export default function Sidebar({ collapsed, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, userRole, creditsBalance } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
    toast.success("Logged out successfully");
  };

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    if (path === "/projects") return location.pathname === "/projects" || location.pathname.startsWith("/project/");
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-slate-950 text-white z-50 flex flex-col transition-all duration-200 ${
        collapsed ? "w-[60px]" : "w-[220px]"
      }`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-3 h-14 border-b border-white/10 shrink-0">
        <img
          src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png"
          alt="Prysh AI"
          className="w-8 h-8 object-contain shrink-0"
        />
        {!collapsed && <span className="font-semibold text-sm tracking-tight truncate">Prysh AI</span>}
      </div>

      {/* New Project shortcut */}
      <div className="px-2 pt-4 pb-2 shrink-0">
        <Button
          onClick={() => navigate("/projects", { state: { openNewProject: true } })}
          size="sm"
          className={`w-full bg-primary hover:bg-primary/90 text-white ${collapsed ? "px-0 justify-center" : "justify-start gap-2"}`}
          data-testid="sidebar-new-project-btn"
        >
          <Plus className="w-4 h-4 shrink-0" />
          {!collapsed && <span className="text-xs">New Project</span>}
        </Button>
      </div>

      {/* Main Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                active
                  ? "bg-white/15 text-white font-medium"
                  : "text-white/60 hover:bg-white/8 hover:text-white/90"
              } ${collapsed ? "justify-center" : ""}`}
              data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {!collapsed && (
                <span className="truncate flex items-center gap-1.5">
                  {item.label}
                  {item.badge && <span className="text-[8px] bg-primary text-white px-1.5 py-0.5 rounded-full font-bold leading-none">{item.badge}</span>}
                </span>
              )}
            </button>
          );
        })}

        {/* Admin section */}
        {isAdmin && (
          <>
            <div className={`pt-4 pb-1 ${collapsed ? "px-2" : "px-2.5"}`}>
              {!collapsed && (
                <span className="text-[10px] uppercase tracking-widest text-white/30 font-semibold">Admin</span>
              )}
              {collapsed && <div className="border-t border-white/10" />}
            </div>
            {ADMIN_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                    active
                      ? "bg-white/15 text-white font-medium"
                      : "text-white/60 hover:bg-white/8 hover:text-white/90"
                  } ${collapsed ? "justify-center" : ""}`}
                  data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </button>
              );
            })}
          </>
        )}
      </nav>

      {/* Bottom section: credits + user + collapse */}
      <div className="border-t border-white/10 px-2 py-3 space-y-2 shrink-0">
        {/* Credits badge */}
        <button
          onClick={() => navigate("/credits")}
          className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm transition-colors bg-orange-500/15 text-orange-400 hover:bg-orange-500/25 ${collapsed ? "justify-center" : ""}`}
          data-testid="sidebar-credits-badge"
          title={collapsed ? `${creditsBalance} credits` : undefined}
        >
          <Wallet className="w-4 h-4 shrink-0" />
          {!collapsed && <span className="text-xs font-medium">{creditsBalance} credits</span>}
        </button>

        {/* User info — clickable to profile */}
        <button
          onClick={() => navigate("/profile")}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors hover:bg-white/8 ${collapsed ? "justify-center" : ""}`}
          data-testid="sidebar-user-profile-btn"
          title={collapsed ? "Profile" : undefined}
        >
          <div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center shrink-0 text-xs font-semibold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0 text-left">
              <p className="text-xs font-medium truncate">{user?.name || "User"}</p>
              <p className="text-[10px] text-white/40 truncate">{userRole}</p>
            </div>
          )}
        </button>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-white/50 hover:bg-red-500/15 hover:text-red-400 transition-colors ${
            collapsed ? "justify-center" : ""
          }`}
          data-testid="sidebar-logout-btn"
          title={collapsed ? "Log out" : undefined}
        >
          <LogOut className="w-4 h-4 shrink-0" />
          {!collapsed && <span>Log out</span>}
        </button>

        {/* Collapse toggle */}
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center py-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors"
          data-testid="sidebar-toggle-btn"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>
    </aside>
  );
}
